import { create } from 'zustand';
import { searchAPI, SearchResult, GlobalSearchResponse, SearchOptions, SearchFilters } from '../lib/searchAPI';

/**
 * `FilterPanel.tsx` держал свой дубль этого union — при третьей площадке он
 * разошёлся бы молча (пропущенная вкладка просто не получила бы своих
 * фильтров, без ошибки типов). Единственный источник — этот файл.
 */
export type SearchTab = 'all' | 'designs' | 'products' | 'wb' | 'ozon' | 'ym' | 'categories' | 'logs';

interface SearchStore {
  query: string;
  results: SearchResult[];
  isSearching: boolean;
  selectedTab: SearchTab;
  totalResults: number;
  resultsByType: Record<string, number>;
  error: string | null;
  
  // Пагинация
  currentPage: number;
  pageSize: number;
  hasMore: boolean;
  
  // Сортировка
  sortBy: 'relevance' | 'name' | 'date' | 'updated';
  sortOrder: 'ASC' | 'DESC';
  
  // Фильтры
  filters: SearchFilters;
  
  // Suggestion для fuzzy matching
  suggestion: string | null;
  
  setQuery: (query: string) => void;
  setSelectedTab: (tab: SearchTab) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSortBy: (sortBy: 'relevance' | 'name' | 'date' | 'updated') => void;
  setSortOrder: (order: 'ASC' | 'DESC') => void;
  setFilters: (filters: SearchFilters) => void;
  search: (query: string, tab?: SearchTab) => Promise<void>;
  clearSearch: () => void;
}

export const useSearchStore = create<SearchStore>((set, get) => ({
  query: '',
  results: [],
  isSearching: false,
  selectedTab: 'all',
  totalResults: 0,
  resultsByType: {},
  error: null,
  currentPage: 1,
  pageSize: 20,
  hasMore: false,
  sortBy: 'relevance',
  sortOrder: 'DESC',
  filters: {},
  suggestion: null,

  setQuery: (query) => set({ query, currentPage: 1 }),

  setFilters: (filters) => {
    set({ filters, currentPage: 1 });
    const currentQuery = get().query || '';
    // Запускаем поиск если есть query или есть активные фильтры
    const hasActiveFilters = filters && Object.keys(filters).some(key => {
      const value = filters[key as keyof SearchFilters];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'number') return true;
      if (typeof value === 'string') return value.trim().length > 0;
      return !!value;
    });
    if ((currentQuery && currentQuery.trim().length > 0) || hasActiveFilters) {
      get().search(currentQuery);
    }
  },

  setSelectedTab: (tab) => {
    set({ selectedTab: tab, currentPage: 1 });
    // Поиск выполняется в компоненте с актуальным запросом из поля ввода
  },

  setPage: (page) => {
    set({ currentPage: page });
    const currentQuery = get().query;
    if (currentQuery && currentQuery.trim().length > 0) {
      get().search(currentQuery);
    }
  },

  setPageSize: (size) => {
    set({ pageSize: size, currentPage: 1 });
    const currentQuery = get().query;
    if (currentQuery && currentQuery.trim().length > 0) {
      get().search(currentQuery);
    }
  },

  setSortBy: (sortBy) => {
    set({ sortBy, currentPage: 1 });
    const currentQuery = get().query;
    if (currentQuery && currentQuery.trim().length > 0) {
      get().search(currentQuery);
    }
  },

  setSortOrder: (sortOrder) => {
    set({ sortOrder, currentPage: 1 });
    const currentQuery = get().query;
    if (currentQuery && currentQuery.trim().length > 0) {
      get().search(currentQuery);
    }
  },

  search: async (query, tab) => {
    const normalizedQuery = query || '';
    const state = get();
    
    // Проверяем, есть ли хотя бы query или активные фильтры
    const hasQuery = normalizedQuery.trim().length > 0;
    const hasActiveFilters = state.filters && Object.keys(state.filters).some(key => {
      const value = state.filters[key as keyof SearchFilters];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'number') return true;
      if (typeof value === 'string') return value.trim().length > 0;
      return !!value;
    });
    
    if (!hasQuery && !hasActiveFilters) {
      set({ results: [], totalResults: 0, resultsByType: {}, currentPage: 1 });
      return;
    }

    const searchTab = tab || get().selectedTab;
    set({ isSearching: true, error: null, query: normalizedQuery });

    try {
      const options: SearchOptions = {
        limit: state.pageSize,
        offset: (state.currentPage - 1) * state.pageSize,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        filters: state.filters,
      };

      let results: SearchResult[] = [];
      let totalResults = 0;
      // Сохраняем предыдущее значение resultsByType, чтобы не терять счетчики при переключении вкладок
      let resultsByType = get().resultsByType || {};
      let hasMore = false;

      let suggestion: string | null = null;

      switch (searchTab) {
        case 'all': {
          const response: GlobalSearchResponse = await searchAPI.globalSearch(normalizedQuery, options);
          results = response?.results || [];
          totalResults = response?.total || 0;
          resultsByType = response?.byType || {};
          hasMore = response?.hasMore || false;
          suggestion = response?.suggestion || null;
          break;
        }
        case 'designs': {
          const response = await searchAPI.searchDesigns(normalizedQuery, options);
          results = response?.items || [];
          totalResults = response?.total || 0;
          hasMore = response?.hasMore || false;
          // Обновляем resultsByType для этой вкладки, сохраняя остальные значения
          if (totalResults > 0) {
            resultsByType = { ...resultsByType, designs: totalResults };
          }
          break;
        }
        case 'products': {
          const response = await searchAPI.searchProducts(normalizedQuery, options);
          results = response?.items || [];
          totalResults = response?.total || 0;
          hasMore = response?.hasMore || false;
          // Обновляем resultsByType для этой вкладки, сохраняя остальные значения
          if (totalResults > 0) {
            resultsByType = { ...resultsByType, products: totalResults };
          }
          break;
        }
        case 'wb': {
          const response = await searchAPI.searchWbProducts(normalizedQuery, options);
          results = response?.items || [];
          totalResults = response?.total || 0;
          hasMore = response?.hasMore || false;
          // Обновляем resultsByType для этой вкладки, сохраняя остальные значения
          if (totalResults > 0) {
            resultsByType = { ...resultsByType, wb_products: totalResults };
          }
          break;
        }
        case 'ozon': {
          const response = await searchAPI.searchOzonProducts(normalizedQuery, options);
          results = response?.items || [];
          totalResults = response?.total || 0;
          hasMore = response?.hasMore || false;
          // Обновляем resultsByType для этой вкладки, сохраняя остальные значения
          if (totalResults > 0) {
            resultsByType = { ...resultsByType, ozon_products: totalResults };
          }
          break;
        }
        case 'ym': {
          const response = await searchAPI.searchYmProducts(normalizedQuery, options);
          results = response?.items || [];
          totalResults = response?.total || 0;
          hasMore = response?.hasMore || false;
          // Обновляем resultsByType для этой вкладки, сохраняя остальные значения
          if (totalResults > 0) {
            resultsByType = { ...resultsByType, ym_products: totalResults };
          }
          break;
        }
        case 'categories': {
          const response = await searchAPI.searchCategories(normalizedQuery, options);
          results = response?.items || [];
          totalResults = response?.total || 0;
          hasMore = response?.hasMore || false;
          // Обновляем resultsByType для этой вкладки, сохраняя остальные значения
          if (totalResults > 0) {
            resultsByType = { ...resultsByType, categories: totalResults };
          }
          break;
        }
        case 'logs': {
          const response = await searchAPI.searchActivityLogs(normalizedQuery, options);
          results = response?.items || [];
          totalResults = response?.total || 0;
          hasMore = response?.hasMore || false;
          // Обновляем resultsByType для этой вкладки, сохраняя остальные значения
          if (totalResults > 0) {
            resultsByType = { ...resultsByType, activity_logs: totalResults };
          }
          break;
        }
      }

      set({ results, totalResults, resultsByType, hasMore, suggestion, isSearching: false });
    } catch (error: any) {
      console.error('Search error:', error);
      set({
        error: error.message || 'Ошибка поиска',
        isSearching: false,
        results: [],
        totalResults: 0,
        hasMore: false,
      });
    }
  },

  clearSearch: () => {
    set({
      query: '',
      results: [],
      totalResults: 0,
      resultsByType: {},
      error: null,
      selectedTab: 'all',
      currentPage: 1,
      hasMore: false,
      suggestion: null,
    });
  },
}));

